/*
 * Shared constants for the AI CBT question generator pages.
 *
 * LEVELS is a copy of the names in droomworkApi/app/Services/Levels.js
 * (L0 to L10). They change only with a migration, so a copy here is simpler
 * than asking the server on every page. Update both together.
 */

export const LEVELS = [
  { code: 0, name: 'Intern / Trainee' },
  { code: 1, name: 'Entry / Graduate' },
  { code: 2, name: 'Associate / Junior' },
  { code: 3, name: 'Intermediate' },
  { code: 4, name: 'Senior' },
  { code: 5, name: 'Lead / Supervisor' },
  { code: 6, name: 'Manager' },
  { code: 7, name: 'Senior Manager' },
  { code: 8, name: 'Head / Director' },
  { code: 9, name: 'VP / General Manager' },
  { code: 10, name: 'C-Suite / MD' },
];

// e.g. 3 -> "L3 Intermediate"
export function levelLabel(code) {
  const level = LEVELS.find((item) => item.code === Number(code));
  if (!level) {
    return `L${code}`;
  }
  return `L${level.code} ${level.name}`;
}

// The three item types. `key` is the value the API expects as item_type,
// and also the Role Library kind used to search for items.
export const ITEM_TYPES = [
  { key: 'specialty', label: 'Specialty', plural: 'specialties' },
  { key: 'tool', label: 'Tool', plural: 'tools' },
  { key: 'skill', label: 'Skill', plural: 'skills' },
];

export function itemTypeLabel(key) {
  const type = ITEM_TYPES.find((item) => item.key === key);
  return type ? type.label : key;
}

// How many questions a run may generate at most. The admin picks one; the
// topic counts may add up to less, never more. 30 is also the API's limit.
export const COUNT_CHOICES = [15, 30];

// Run status -> the words an admin sees and a chip colour.
// `working` means the cron is still busy with the run.
export const RUN_STATUS = {
  queued: { label: 'Waiting to be written', tone: 'slate', working: true },
  writing: { label: 'Being written', tone: 'amber', working: true },
  written: { label: 'Waiting to be checked', tone: 'amber', working: true },
  checking: { label: 'Being checked', tone: 'amber', working: true },
  ready: { label: 'Ready for review', tone: 'navy', working: false },
  sent_back: { label: 'Sent back', tone: 'red', working: false },
  submitted: { label: 'Waiting for approval', tone: 'orange', working: false },
  approved: { label: 'Approved', tone: 'green', working: false },
  failed: { label: 'Failed', tone: 'red', working: false },
  cancelled: { label: 'Cancelled', tone: 'slate', working: false },
};

// The checker's label on a draft (cbt_question_drafts.checker_result)
export const CHECKER_RESULT = {
  agreed: { label: 'Checker agreed', tone: 'green' },
  disagreed: { label: 'Checker disagreed', tone: 'red' },
  flagged: { label: 'Checker flagged an issue', tone: 'amber' },
  unchecked: { label: 'Not checked', tone: 'slate' },
  edited: { label: 'Edited', tone: 'navy' },
  confirmed: { label: 'Confirmed by admin', tone: 'green' },
};

// Same values as CbtQuestionDraft.deleteReasons in the API
export const DELETE_REASONS = [
  { value: 'wrong_answer', label: 'Wrong answer' },
  { value: 'ambiguous', label: 'Ambiguous' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'off_topic', label: 'Off topic' },
  { value: 'off_level', label: 'Wrong level' },
  { value: 'too_easy', label: 'Too easy' },
  { value: 'other', label: 'Other' },
];

export function deleteReasonLabel(value) {
  const reason = DELETE_REASONS.find((item) => item.value === value);
  return reason ? reason.label : value;
}

export const TONE_CLASS = {
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  navy: 'bg-brand-navy-100 text-brand-navy border-brand-navy-100',
  orange: 'bg-brand-orange-100 text-brand-orange-600 border-brand-orange-100',
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

// The API answers errors as { status: false, message }. Its messages are
// written for admins, so they are shown as they are.
export function describeError(error) {
  if (error && error.data && error.data.message) {
    return error.data.message;
  }
  if (error && error.status === 'FETCH_ERROR') {
    return 'Could not reach the server. Check your connection and try again.';
  }
  return 'Something went wrong. Please try again.';
}

// The item a run is for: the API returns it under one of three relations
export function runItemName(run) {
  if (run.item_name) {
    return run.item_name;
  }
  if (run.language) {
    return run.language.name;
  }
  if (run.framework) {
    return run.framework.name;
  }
  if (run.skill) {
    return run.skill.name;
  }
  return 'Unknown item';
}

export const inputClass =
  'h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
export const selectClass =
  'h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
